import { type FormikErrors, type FormikTouched, useFormik } from 'formik'
import { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { RoutePath } from '@percent/cause-dashboard/app/routes/Routes'
import { useCausesPortalAnalytics, useMutation, useMyCountryFromIp } from '@percent/cause-dashboard/common/hooks'
import { useServices } from '@percent/cause-dashboard/context/serviceContext/ServiceContext'
import type { CreateVolunteeringEventPayload } from '@percent/cause-dashboard/services/volunteering/volunteeringService.types'
import {
  type UseErrorModalReturn,
  type UseSuccessModalReturn,
  type UseModalReturn,
  useCoverImageField,
  useErrorModal,
  useSuccessModal,
  useModal
} from '@percent/domain/giving'
import { type OpportunityLocationDto, OpportunityLocationTypeDto, OpportunityType } from '@percent/domain/volunteering'

import { FILE_SIZE_2MB, FILE_SIZE_2MB_HUMAN, ACCEPTED_BANNER_IMG_FORMATS_HUMAN } from '../constants'
import { useMapError } from '../hooks/useMapError'
import { usePartnerConfig, type UsePartnerConfigResult } from '../hooks/usePartnerConfig'
import { addProtocolToWebsiteUrl } from '../utils/addProtocolToUrl'
import { formatStartEndDate } from '../utils/formatStartEndDate'
import type { CreateOpportunityFormFields } from './CreateOpportunityForm.types'
import { useOpportunityFormValidationSchema } from '../hooks/useOpportunityFormValidation'

export const getInitialValues = (defaultCountryCode: string): CreateOpportunityFormFields => ({
  partnerId: '',
  image: '',
  name: '',
  date: undefined,
  startTime: '',
  endTime: '',
  description: '',
  locationType: '',
  locationUrl: '',
  participantSpots: undefined,
  activities: [],
  skills: [],
  addressLineOne: '',
  addressLineTwo: '',
  city: '',
  country: defaultCountryCode,
  zipCode: '',
  long: 0,
  lat: 0,
  notificationsEnabled: true,
  targetCountries: []
})

const mapFormValuesToCreateEventPayload = (
  formValues: CreateOpportunityFormFields,
  coverImage?: File
): CreateVolunteeringEventPayload => {
  const {
    date,
    startTime,
    endTime,
    name,
    description,
    activities,
    skills,
    partnerId,
    participantSpots,
    notificationsEnabled,
    targetCountries,
    locationType,
    locationUrl,
    addressLineOne,
    addressLineTwo,
    city,
    country,
    zipCode,
    long,
    lat
  } = formValues

  const { startDate, endDate } = formatStartEndDate(date, startTime, endTime)

  const trimmedLocationUrl = locationUrl.trim()

  const location: OpportunityLocationDto =
    locationType === OpportunityLocationTypeDto.Virtual
      ? {
          type: OpportunityLocationTypeDto.Virtual,
          link: trimmedLocationUrl.length ? addProtocolToWebsiteUrl(trimmedLocationUrl) : undefined
        }
      : {
          type: OpportunityLocationTypeDto.Offline,
          addressLineOne,
          addressLineTwo,
          city,
          country,
          zipCode,
          long,
          lat
        }

  return {
    type: OpportunityType.Event,
    name,
    description,
    partnerId,
    location,
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    startDate: startDate!,
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    endDate: endDate!,
    participantSpots,
    timeTracking: 'automatic',
    notificationsEnabled,
    skills,
    activities,
    image: coverImage,
    targetCountries
  }
}

export interface UseCreateOpportunityFormResult {
  coverImage: File | undefined
  coverImageError: string | undefined
  handleCoverImageChange: (file: File | string, fileName?: string) => Promise<void>
  isValid: boolean
  errors: FormikErrors<CreateOpportunityFormFields>
  values: CreateOpportunityFormFields
  handleChange: (e: React.ChangeEvent<Element>) => void
  handleSubmit: (e?: React.FormEvent<HTMLFormElement> | undefined) => void
  handleBlur: (e: React.FocusEvent<Element>) => void
  touched: FormikTouched<CreateOpportunityFormFields>
  isSubmitting: boolean
  setFieldValue: <T extends keyof CreateOpportunityFormFields>(
    field: T,
    value: CreateOpportunityFormFields[T],
    shouldValidate?: boolean
  ) => Promise<void | FormikErrors<CreateOpportunityFormFields>>
  setFieldTouched: (
    field: string,
    touched?: boolean | undefined,
    shouldValidate?: boolean | undefined
  ) => Promise<void | FormikErrors<CreateOpportunityFormFields>>
  dirty: boolean
  formValid: boolean
  validateField: (name: string) => Promise<void> | Promise<string | undefined>
  validateFields: (keys: string[]) => Promise<void>
  partnerConfig: UsePartnerConfigResult
  successModalProps: UseSuccessModalReturn
  errorModalProps: UseErrorModalReturn
  loadPartnerConfigErrorModal: UseErrorModalReturn
  cancellationModalProps: UseModalReturn
  handleCancelForm: VoidFunction
  setDateValue: (value: CreateOpportunityFormFields['date']) => void
  setStartTime: (value: CreateOpportunityFormFields['startTime']) => void
  setEndTime: (value: CreateOpportunityFormFields['endTime']) => void
  setDateTouched: () => void
  setStartTimeTouched: () => void
  setEndTimeTouched: () => void
  setSkills: (value: CreateOpportunityFormFields['skills']) => void
  setActivities: (value: CreateOpportunityFormFields['activities']) => void
  setParticipantSpots: (value: CreateOpportunityFormFields['participantSpots']) => void
  setNotificationsEnabled: (value: CreateOpportunityFormFields['notificationsEnabled']) => void
  setDescription: (value: CreateOpportunityFormFields['description']) => void
  setDescriptionTouched: () => void
  setPartnerId: (value: CreateOpportunityFormFields['partnerId']) => void
}

export const useCreateOpportunityForm = (): UseCreateOpportunityFormResult => {
  const { t } = useTranslation()
  const { track } = useCausesPortalAnalytics()
  const { volunteeringService } = useServices()
  const navigate = useNavigate()
  const defaultCountryCode = useMyCountryFromIp({ useLocalCache: true })?.country ?? 'GBR'
  const validationSchema = useOpportunityFormValidationSchema()
  const mapError = useMapError()

  const successModalProps = useSuccessModal()
  const errorModalProps = useErrorModal()
  const loadPartnerConfigErrorModal = useErrorModal()
  const cancellationModalProps = useModal()

  const { coverImage, coverImageError, setCoverImageError, handleCoverImageChange } = useCoverImageField({
    maxFileSize: FILE_SIZE_2MB,
    validationErrorRequiredField: t('validation.requiredField'),
    validationErrorInvalidFileType: t('validation.invalidFileType', {
      fileFormats: ACCEPTED_BANNER_IMG_FORMATS_HUMAN
    }),
    validationErrorFileTooLarge: t('validation.fileTooLarge', { fileSize: FILE_SIZE_2MB_HUMAN })
  })

  const [{ isLoading: isSubmitting }, { apiFunc: createOpportunityMutate }] = useMutation(
    volunteeringService.createOpportunity,
    successModalProps.openModal,
    error => {
      const { errorMessage, reason } = mapError(error)
      console.error(reason, errorMessage)
      errorModalProps.setErrorMsg(errorMessage)
      errorModalProps.openModal()
    }
  )

  const {
    isValid,
    errors,
    values,
    handleChange,
    handleSubmit,
    handleBlur,
    touched,
    setFieldValue,
    setFieldTouched,
    dirty,
    validateForm,
    validateField
  } = useFormik({
    initialValues: getInitialValues(defaultCountryCode),
    validationSchema,
    onSubmit: async data => {
      const targetCountries = data.targetCountries.length ? data.targetCountries : partnerConfig.allCountries

      try {
        createOpportunityMutate(mapFormValuesToCreateEventPayload({ ...data, targetCountries }, coverImage))
      } catch (e) {
        console.error(e)
      }

      track('Volunteering Opportunity Create Completed', {
        properties: {
          opportunityLocationType: data.locationType,
          opportunityType: OpportunityType.Event
        }
      })
    },
    validateOnBlur: true,
    validateOnChange: true
  })

  const setDateValue = useCallback(
    (value: CreateOpportunityFormFields['date']) => setFieldValue('date', value),
    [setFieldValue]
  )
  const setStartTime = useCallback(
    (value: CreateOpportunityFormFields['startTime']) => setFieldValue('startTime', value),
    [setFieldValue]
  )
  const setEndTime = useCallback(
    (value: CreateOpportunityFormFields['endTime']) => setFieldValue('endTime', value),
    [setFieldValue]
  )
  const setDateTouched = useCallback(() => setFieldTouched('date', true), [setFieldTouched])
  const setStartTimeTouched = useCallback(() => setFieldTouched('startTime', true), [setFieldTouched])
  const setEndTimeTouched = useCallback(() => setFieldTouched('endTime', true), [setFieldTouched])
  const setSkills = useCallback(
    (value: CreateOpportunityFormFields['skills']) => setFieldValue('skills', value),
    [setFieldValue]
  )
  const setActivities = useCallback(
    (value: CreateOpportunityFormFields['activities']) => setFieldValue('activities', value),
    [setFieldValue]
  )
  const setParticipantSpots = useCallback(
    (value: CreateOpportunityFormFields['participantSpots']) => setFieldValue('participantSpots', value),
    [setFieldValue]
  )
  const setNotificationsEnabled = useCallback(
    (value: CreateOpportunityFormFields['notificationsEnabled']) => setFieldValue('notificationsEnabled', value),
    [setFieldValue]
  )
  const setDescription = useCallback(
    (value: CreateOpportunityFormFields['description']) => setFieldValue('description', value),
    [setFieldValue]
  )
  const setDescriptionTouched = useCallback(() => setFieldTouched('description', true), [setFieldTouched])
  const setPartnerId = useCallback(
    (value: CreateOpportunityFormFields['partnerId']) => setFieldValue('partnerId', value),
    [setFieldValue]
  )

  const validateFields = useCallback(
    async (keys: string[]) => {
      await Promise.all(keys.map(key => validateField(key)))
    },
    [validateField]
  )

  const partnerConfig = usePartnerConfig(values.partnerId || undefined)

  useEffect(() => {
    if (partnerConfig.errors?.length) {
      console.error(partnerConfig.errors)
      loadPartnerConfigErrorModal.setErrorMsg(t('errorMessage.somethingWentWrong'))
      loadPartnerConfigErrorModal.openModal()
    }
  }, [partnerConfig.errors, loadPartnerConfigErrorModal, t])

  const formValid = isValid && dirty && !!coverImage && !coverImageError && !isSubmitting

  useEffect(() => {
    if (touched.image && !coverImage) {
      setCoverImageError(t('validation.requiredField'))
    }
  }, [coverImage, t, touched.image, setCoverImageError])

  useEffect(() => {
    validateForm()
  }, [validateForm, values.locationType])

  const handleSuccessModalClose = () => {
    successModalProps.closeModal()
    navigate(RoutePath.VOLUNTEERING)
  }

  const handleCancelForm = () => {
    if (dirty || !!coverImage) {
      cancellationModalProps.openModal()
    } else {
      navigate(-1)
    }
  }

  return {
    coverImage,
    coverImageError,
    handleCoverImageChange,
    isValid,
    errors,
    values,
    handleChange,
    handleSubmit,
    handleBlur,
    touched,
    isSubmitting,
    setFieldValue,
    setFieldTouched,
    setDateValue,
    setStartTime,
    setEndTime,
    setDateTouched,
    setStartTimeTouched,
    setEndTimeTouched,
    setSkills,
    setActivities,
    setParticipantSpots,
    setNotificationsEnabled,
    setDescription,
    setDescriptionTouched,
    setPartnerId,
    dirty,
    formValid,
    validateField,
    validateFields,
    partnerConfig,
    successModalProps: { ...successModalProps, closeModal: handleSuccessModalClose },
    errorModalProps,
    loadPartnerConfigErrorModal,
    cancellationModalProps,
    handleCancelForm
  }
}
